// KeyPointVerticalMobileComponent.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import { linkProps } from '../../../utils/appLink';
import renderKeyPointText from './renderKeyPointText';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

// Cloudflare mobile KeyPoint vertical vector
const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVerticalVector.svg';

const KeyPointVerticalMobileComponent = ({ data }) => {
  const { title, description, footer, steps, descriptionLink } = data;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        maxWidth: '100vw',
        backgroundColor: '#F3F6F6',
        display: 'flex',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ width: '100%' }}>
        {/* Blue Box */}
        <Box
          sx={{
            width: 'calc(100% - 32px)',
            backgroundColor: '#162F3B',
            borderRadius: '34px',
            padding: '40px 32px',
            mx: '16px',
            zIndex: 2,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '32px',
          }}
        >
          <ScrollFadeWrapper variant="text">
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                lineHeight: '2.5rem',
                color: '#FFFFFF',
                letterSpacing: '-0.03em',
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
          </ScrollFadeWrapper>

          {description && (
            <ScrollFadeWrapper variant="text" delay={0.2}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1.125rem',
                  lineHeight: '1.75rem',
                  color: '#FFFFFF',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                }}
              >
                {description
                  ?.split(/(\*\*[^*]+\*\*|<G>[^<]+<G>)/g)
                  .map((part, i) => {
                    if (/^\*\*[^*]+\*\*$/.test(part)) {
                      return (
                        <strong key={i} style={{ fontWeight: 700 }}>
                          {part.slice(2, -2)}
                        </strong>
                      );
                    } else if (/^<G>[^<]+<G>$/.test(part)) {
                      return (
                        <span key={i} style={{ color: '#C7CA00' }}>
                          <LinkWithIconComponent
                            {...linkProps(descriptionLink)}
                            showIcon={Boolean(descriptionLink)}
                            target="_blank"
                            iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
                            sx={{
                              color: '#C7CA00',
                              fontWeight: 500,
                            }}
                          >
                            {part.slice(3, -3)}
                          </LinkWithIconComponent>
                        </span>
                      );
                    }
                    return part;
                  })}
              </Typography>
            </ScrollFadeWrapper>
          )}

          {footer && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.75rem',
                lineHeight: '1.25rem',
                color: '#FFFFFF',
                letterSpacing: '-0.01em',
                textAlign: 'center',
              }}
            >
              {footer}
            </Typography>
          )}
        </Box>

        {/* Steps */}
        <Box
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            borderRadius: '48px',
            padding: '40px 32px 32px 32px',
            boxSizing: 'border-box',
            zIndex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            mt: '-32px',
            boxShadow: '0px 3px 8px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB133',
          }}
        >
          {steps.map(({ icon, title, text }, index) => (
            <ScrollFadeWrapper key={index} delay={0.4 + index * 0.08}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  mt: index === 0 ? '40px' : 0,
                }}
              >
                <Box
                  sx={{ position: 'relative', width: '80px', height: '80px' }}
                >
                  <Image
                    width={80}
                    height={80}
                    src={KEYPOINT_VECTOR_URL}
                    alt="Key point illustration (vertical)"
                    sx={{ width: '100%', height: '100%' }}
                  />
                  <Image
                    width={80}
                    height={80}
                    src={icon}
                    alt={`icon-${index}`}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '50%',
                      height: '50%',
                    }}
                  />
                </Box>

                <Typography
                  component="h3"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',
                  }}
                >
                  {title}
                </Typography>

                {text && (
                  <Box>
                    {renderKeyPointText(text, {
                      paragraphSx: {
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        color: '#162F3B',
                        textAlign: 'left',
                        letterSpacing: '-0.02em',
                      },
                      headingSx: {
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        color: '#162F3B',
                        textAlign: 'left',
                        letterSpacing: '-0.02em',
                      },
                      listItemSx: {
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        color: '#162F3B',
                        textAlign: 'left',
                        letterSpacing: '-0.02em',
                      },
                    })}
                  </Box>
                )}
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default KeyPointVerticalMobileComponent;
